import { useState, useEffect, useRef } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { ChatMessage } from '../types';

const CHAT_COL     = 'team_chats';
const MAX_MESSAGES = 120;
const SAVE_DELAY   = 1200;

function filterSaveable(msgs: ChatMessage[]): ChatMessage[] {
  return msgs.filter(m => m.content?.trim() || m.images?.length);
}

/**
 * Persiste el historial de chat Oski en Firestore por usuario.
 * - Carga al montar
 * - Guarda con debounce cada vez que messages cambia
 * - Guarda inmediatamente al desmontar (para no perder mensajes al navegar)
 * - Solo guarda si hay más de 1 mensaje (el greeting solo es efímero)
 */
export function useChatHistory(uid: string | undefined) {
  const [messages, setMessages]             = useState<ChatMessage[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const ready      = useRef(false);
  const saveRef    = useRef<ReturnType<typeof setTimeout>>();
  const messagesRef = useRef<ChatMessage[]>(messages);

  // Mantener ref sincronizada para poder guardar en el unmount
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // ── Carga inicial ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!uid) {
      setLoadingHistory(false);
      return;
    }

    let cancelled = false;

    getDoc(doc(db, CHAT_COL, uid))
      .then(snap => {
        if (cancelled) return;
        if (!snap.exists()) return;

        const saved = (snap.data().messages ?? []) as ChatMessage[];
        if (saved.length > 0) setMessages(saved);
      })
      .catch(err => console.error('[ChatHistory] Load failed:', err.message))
      .finally(() => {
        if (!cancelled) {
          ready.current = true;
          setLoadingHistory(false);
        }
      });

    return () => { cancelled = true; };
  }, [uid]);

  // ── Persistencia con debounce ─────────────────────────────────────────────
  useEffect(() => {
    if (!uid || !ready.current) return;

    clearTimeout(saveRef.current);
    saveRef.current = setTimeout(() => {
      save(uid, messagesRef.current);
    }, SAVE_DELAY);

    return () => clearTimeout(saveRef.current);
  }, [messages, uid]);

  // ── Guardar al desmontar (navegar fuera del chat) ─────────────────────────
  useEffect(() => {
    if (!uid) return;
    return () => {
      if (!ready.current) return;
      clearTimeout(saveRef.current);
      save(uid, messagesRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  return { messages, setMessages, loadingHistory };
}

function save(uid: string, msgs: ChatMessage[]) {
  const saveable = filterSaveable(msgs).slice(-MAX_MESSAGES);
  // No guardar si solo es el greeting (1 mensaje del assistant)
  if (saveable.length <= 1 && saveable[0]?.role === 'assistant') return;

  setDoc(
    doc(db, 'team_chats', uid),
    { messages: saveable, updatedAt: new Date().toISOString() },
    { merge: true }
  ).catch(err => console.error('[ChatHistory] Save failed:', err.message));
}
